/**
 * Позиция прокрутки экрана между переходами (D-03 §3.5 п. 1).
 *
 * Роутер пересоздаёт экран при каждом переходе, поэтому возврат из вложенного
 * экрана показывал список с самого верха — хотя уходили из его середины.
 * Позиции снимаются в момент смены записи истории (DOM ещё прежний, App.tsx)
 * и возвращаются, когда экран этой записи снова смонтирован (ScreenFrame).
 *
 * Областей прокрутки на экране может быть несколько (содержимое `Screen` и
 * виртуализированный список внутри него), поэтому хранится массив смещений в
 * порядке обхода документа — сопоставление идёт по этому порядку.
 *
 * Список набирает высоту не сразу: виртуализация измеряет строки после первой
 * отрисовки, а данные приходят из реплики. Поэтому позиция доводится несколько
 * кадров, пока экран не вырастет до нужной высоты, но не дольше `RESTORE_MS` и
 * не после того, как человек сам тронул список.
 *
 * Память живёт в модуле: она описывает текущий сеанс работы с окном.
 */
import { NAV_SCREEN_ATTR } from './screen-snapshots'

/** Стоит на области прокрутки, позицию которой стоит помнить. */
export const SCREEN_SCROLL_ATTR = 'data-screen-scroll'

/** Столько записей истории помнится — как и снимков экранов. */
const MAX_ENTRIES = 8

/** Дольше этого доводить позицию незачем: экран уже показан человеку. */
const RESTORE_MS = 600

const store = new Map<string, number[]>()

function scrollers(root: ParentNode): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(`[${SCREEN_SCROLL_ATTR}]`)]
}

/** Запоминает прокрутку текущего экрана под ключом записи истории, с которой уходим. */
export function captureScreenScroll(key: string | null): void {
  if (!key || typeof document === 'undefined') return

  const screen = document.querySelector<HTMLElement>(`[${NAV_SCREEN_ATTR}]`)
  if (!screen) return

  const offsets = scrollers(screen).map((element) => element.scrollTop)
  if (offsets.length === 0) return

  store.set(key, offsets)
  while (store.size > MAX_ENTRIES) {
    const oldest = store.keys().next().value
    if (oldest === undefined) break
    store.delete(oldest)
  }
}

/**
 * Возвращает прокрутку экрана этой записи истории. Результат — функция отмены:
 * её вызывает рамка при размонтировании.
 */
export function restoreScreenScroll(key: string | null, root: HTMLElement): () => void {
  const offsets = key ? store.get(key) : undefined
  if (!offsets || offsets.every((offset) => offset === 0)) return () => undefined

  let frame = 0
  let stopped = false
  const deadline = Date.now() + RESTORE_MS
  /** Снятое якорение: пока позицию доводим, Chrome не должен подправлять её сам. */
  const anchored: HTMLElement[] = []

  const stop = (): void => {
    if (stopped) return
    stopped = true
    if (frame) cancelAnimationFrame(frame)
    frame = 0
    for (const element of anchored) element.style.removeProperty('overflow-anchor')
    anchored.length = 0
    root.removeEventListener('pointerdown', stop)
    root.removeEventListener('wheel', stop)
  }

  /** Ставит позиции; возвращает true, когда всем хватило высоты. */
  const apply = (): boolean => {
    let complete = true

    scrollers(root).forEach((element, index) => {
      const wanted = offsets[index]
      if (wanted === undefined || wanted === 0) return

      if (!anchored.includes(element)) {
        element.style.setProperty('overflow-anchor', 'none')
        anchored.push(element)
      }

      const max = Math.max(0, element.scrollHeight - element.clientHeight)
      const next = Math.min(wanted, max)
      if (Math.abs(element.scrollTop - next) > 1) element.scrollTop = next
      // Высота ещё не набралась — список дорисовывается, позицию нужно довести
      if (next < wanted - 1) complete = false
    })

    return complete
  }

  const tick = (): void => {
    frame = 0
    if (stopped) return
    if (apply() || Date.now() > deadline) {
      stop()
      return
    }
    frame = requestAnimationFrame(tick)
  }

  // Человек тронул список — доводка прекращается, чтобы не спорить с ним.
  root.addEventListener('pointerdown', stop, { passive: true })
  root.addEventListener('wheel', stop, { passive: true })

  if (!apply()) frame = requestAnimationFrame(tick)
  else stop()

  return stop
}

/** Смена пользователя: чужие позиции не восстанавливаются. */
export function clearScreenScroll(): void {
  store.clear()
}
